import { forwardRef, useImperativeHandle, useRef } from 'react'

import { Input, type InputProps, type InputRef } from 'antd'

import {
  createCustomShowCountFormatter,
  handleBlur,
  handleChange,
  handleKeyDown,
  handlePaste,
} from '../../lib/self-validate-fields'

type AutoInputProps = Omit<InputProps, 'onChange'> & {
  blockClipboard?: boolean
  value: string | string[]
  onChange: (value: string) => void
}

export type SelfValidateInputRef = {
  focus: () => void
  getSelection: () => { start: number; end: number }
  setSelection: (pos: number) => void
}

export const SelfValidateInput = forwardRef<
  SelfValidateInputRef,
  AutoInputProps
>(({ blockClipboard = false, value, maxLength, onChange, ...rest }, ref) => {
  const inputRef = useRef<InputRef>(null)

  const stringValue: string = Array.isArray(value) ? value.join(', ') : value

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus()
    },
    getSelection: () => {
      const el = inputRef.current?.input
      return {
        start: el?.selectionStart ?? stringValue.length,
        end: el?.selectionEnd ?? stringValue.length,
      }
    },
    setSelection: (pos) => {
      const el = inputRef.current?.input
      if (el) {
        el.selectionStart = el.selectionEnd = pos
      }
    },
  }))

  const isError = maxLength && stringValue.length > maxLength

  return (
    <Input
      {...rest}
      ref={inputRef}
      value={stringValue}
      onChange={(e) => {
        handleChange(e, onChange)
      }}
      onKeyDown={(e) => {
        rest.onKeyDown?.(e)
        handleKeyDown(e, stringValue)
      }}
      onPaste={(e) => {
        rest.onPaste?.(e)
        handlePaste(e, stringValue, maxLength, blockClipboard, onChange)
      }}
      onBlur={(e) => {
        rest.onBlur?.(e)
        handleBlur(stringValue, onChange)
      }}
      maxLength={undefined}
      showCount={createCustomShowCountFormatter(maxLength)}
      status={isError ? 'error' : undefined}
    />
  )
})

SelfValidateInput.displayName = 'SelfValidateInput'
